import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Typography } from '@mui/material';
import MLButton from '../Button/Button';
import MLCustomIcon from '../CustomIcon';
import MLBadge from '../Badge/Badge';
import MLTooltip from '../Tooltip/Tooltip';
import MLLinearProgress from '../LinearProgress/LinearProgress';
import NoDataFound from '../NoDataFound/NoDataFound';
import MLLoader from '../Loader/Loader';
import MLMenu from '../Menu/Menu';
import MLDropdown from '../Dropdown/Dropdown';
import MLTextField from '../TextField/TextField';
import MLCheckbox from '../Checkbox/Checkbox';
import MultiSegmentProgressBar from '../MultiSegmentLinearProgress/MultiSegmentLinearProgress';
import './Table.module.scss';

const MLTable = ({
  columns,
  rows,
  classList,
  isRowClickable = false,
  rowHandler = undefined,
  children,
  includeNoDataFoundBtn = false,
  noDataFoundBtnText = 'Button',
  noDataFoundBtnClick = undefined,
  noDataFoundMsg = 'No data found!',
  isDataLoading = false,
  isManageNoFoundRows = false,
  showNoFoundRowMsg = '',
  noFoundRowClassList = '',
  headerClassList = '',
  isGroupChange = false,
  isCR = false,
  setIsGroupChange = () => {},
  setselectedClass = () => {},
  selectedClass = '',
}) => {
  const [sortedRows, setSortedRows] = useState([]);
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortedColumn, setSortedColumn] = useState('student-name');

  useEffect(() => {
    setSortedRows(rows);

    if (rows && rows.length > 0 && columns[0].hasSortingDropdown) {
      if (isGroupChange && isCR) {
        handleSort('student-name', 'asc');
        setSortedColumn('student-name');
        setSortDirection('asc');
        setIsGroupChange(false);
      } else if (sortedColumn !== 'student-name' || sortDirection !== 'asc') {
        handleSort(sortedColumn, sortDirection);
      } else {
        handleSort('student-name', 'asc');
        setSortedColumn('student-name');
        setSortDirection('asc');
      }
    }
  }, [rows]);

  const handleSort = (columnId, sortOrder) => {
    setSortDirection(sortOrder);
    setSortedColumn(columnId);

    const sortedData = [...rows].sort((a, b) => {
      const valueA = a[columnId];
      const valueB = b[columnId];

      //Skip Averages row and sort student-name based on surname
      if (columnId === 'student-name') {
        const normalizedA = valueA.trim().toLowerCase();
        const normalizedB = valueB.trim().toLowerCase();

        const forttedA = normalizedA.split(' ')[1];
        const forttedB = normalizedB.split(' ')[1];

        if (normalizedA === 'averages:' && normalizedB === 'averages:') {
          return 0;
        }
        if (normalizedA === 'averages:') {
          return -1;
        }
        if (normalizedB === 'averages:') {
          return 1;
        }
        if (sortOrder === 'asc') {
          if (forttedA < forttedB) return -1;
          if (forttedA > forttedB) return 1;
          return 0;
        } else {
          if (forttedB < forttedA) return -1;
          if (forttedB > forttedA) return 1;
          return 0;
        }
      }

      if (columnId === 'totalPer') {
        if (valueA.rowType === 'average' || valueB.rowType === 'average') {
          return 0;
        }

        if (valueA?.score === '' && valueB?.score !== '') {
          return 1; // 'a' ('') comes after 'b' (non-'')
        }
        if (valueA?.score !== '' && valueB?.score === '') {
          return -1; // 'a' (non-'') comes before 'b' ('')
        }
        if (valueA?.score === '' && valueB?.score === '') {
          return 0; // Maintain relative order of ''s
        }

        const propA = valueA?.score === '' ? -1 : parseFloat(valueA.score);
        const propB = valueB?.score === '' ? -1 : parseFloat(valueB.score);

        if (sortOrder === 'asc') {
          if (propA < propB) return -1;
          if (propA > propB) return 1;
          return 0;
        } else {
          if (propB < propA) return -1;
          if (propB > propA) return 1;
          return 0;
        }
      }

      if (columnId === 'date') {
        if (valueA === ' ' || valueB === ' ') {
          return 0;
        }

        if ((valueA ?? '') === '' && (valueB ?? '') !== '') {
          return 1; // 'a' ('') comes after 'b' (non-'')
        }
        if ((valueA ?? '') !== '' && (valueB ?? '') === '') {
          return -1; // 'a' (non-'') comes before 'b' ('')
        }
        if ((valueA ?? '') === '' && (valueB ?? '') === '') {
          return 0; // Maintain relative order of ''s
        }

        const propA =
          valueA.substring(6, 8) +
          valueA.substring(3, 5) +
          valueA.substring(0, 2);
        const propB =
          valueB.substring(6, 8) +
          valueB.substring(3, 5) +
          valueB.substring(0, 2);

        if (sortOrder === 'asc') {
          if (propA < propB) return 1;
          if (propA > propB) return -1;
          return 0;
        } else {
          if (propB < propA) return 1;
          if (propB > propA) return -1;
          return 0;
        }
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        const normalizedA = valueA.trim().toLowerCase();
        const normalizedB = valueB.trim().toLowerCase();

        if (valueA.rowType === 'average' || valueB.rowType === 'average') {
          return 0;
        }

        if (
          (columnId === 'totalPer' && rows[0][columnId] === normalizedA) ||
          (columnId === 'totalPer' && rows[0][columnId] === normalizedB)
        ) {
          return 0;
        }

        if (normalizedA === '' && normalizedB !== '') {
          return 1; // 'a' ('') comes after 'b' (non-'')
        }
        if (normalizedA !== '' && normalizedB === '') {
          return -1; // 'a' (non-'') comes before 'b' ('')
        }
        if (normalizedA === '' && normalizedB === '') {
          return 0; // Maintain relative order of ''s
        }

        if (sortOrder === 'asc') {
          if (normalizedA < normalizedB) return -1;
          if (normalizedA > normalizedB) return 1;
          return 0;
        } else {
          if (normalizedB < normalizedA) return -1;
          if (normalizedB > normalizedA) return 1;
          return 0;
        }
      }

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        if (valueA.rowType === 'average' || valueB.rowType === 'average') {
          return 0;
        }

        if (
          (columnId === 'total' && rows[0][columnId] === valueA) ||
          (columnId === 'total' && rows[0][columnId] === valueB)
        ) {
          return 0;
        }

        if (valueA === null && valueB !== null) {
          return 1; // 'a' (null) comes after 'b' (non-null)
        }
        if (valueA !== null && valueB === null) {
          return -1; // 'a' (non-null) comes before 'b' (null)
        }
        if (valueA === null && valueB === null) {
          return 0; // Maintain relative order of nulls
        }

        return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
      }

      if (typeof valueA === 'object' && typeof valueB === 'object') {
        if (valueA.rowType === 'average' || valueB.rowType === 'average') {
          return 0;
        }

        if (
          (valueA?.score === '' && valueB?.score !== '') ||
          (valueA?.score === null && valueB?.score !== null)
        ) {
          return 1; // 'a' (null) comes after 'b' (non-null)
        }
        if (
          (valueA?.score !== '' && valueB?.score === '') ||
          (valueA?.score !== null && valueB?.score === null)
        ) {
          return -1; // 'a' (non-null) comes before 'b' (null)
        }
        if (
          (valueA?.score === '' && valueB?.score === '') ||
          (valueA?.score === null && valueB?.score === null)
        ) {
          return 0; // Maintain relative order of nulls
        }

        const propA = valueA?.score === '-' ? 0 : valueA?.score || '';
        const propB = valueB?.score === '-' ? 0 : valueB?.score || '';

        if (sortOrder === 'asc') {
          if (propA < propB) return -1;
          if (propA > propB) return 1;
          return 0;
        } else {
          if (propB < propA) return -1;
          if (propB > propA) return 1;
          return 0;
        }
      }

      return null;
    });

    setSortedRows(sortedData);
  };

  if (isDataLoading) {
    return <MLLoader />;
  }

  if (!isManageNoFoundRows && !(rows ?? []).length && !isDataLoading) {
    return (
      <>
        <NoDataFound
          message={noDataFoundMsg}
          includeBtn={includeNoDataFoundBtn}
          btnText={noDataFoundBtnText}
          onBtnClick={noDataFoundBtnClick}
        />
        {children}
      </>
    );
  }

  const dropdownHandler = (columnId, sortOrder) => {
    if (columnId === 'class') {
      setselectedClass(sortOrder);
      // setSortDirection(sortOrder);
      console.log('🚀 ~ dropdownHandler ~ sortOrder:', sortOrder);
    } else {
      setSortedColumn(columnId);
      setSortDirection(sortOrder);
      handleSort(columnId, sortOrder);
    }
  };

  return (
    <>
      <div className={`table-wrap ${classList}`}>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                      width: column.width ? column.width : '',
                    }}
                    // onClick={
                    //   column.isSortable
                    //     ? () => handleSort(column.id)
                    //     : undefined
                    // }
                    className={`sortable-column ${
                      column.columnClassList ? column.columnClassList : ''
                    }`}
                  >
                    <div className="header-cell-wrapper">
                      {column.hasSortingDropdown && (
                        <MLDropdown
                          options={column.sortingOptions}
                          selectedOption={(sortOrder) => {
                            dropdownHandler(column.id, sortOrder);
                          }}
                          defaultSelectedOption={
                            sortedColumn === column.id
                              ? sortDirection
                              : column.id === 'class'
                                ? selectedClass
                                : ''
                          }
                          isPlaceHolderClass={true}
                          popoverClass="sorting-dropdown-popover"
                          dropdownIcon={
                            column.id === 'class'
                              ? 'Dropdown-icon'
                              : 'Bars-arrow-down'
                          }
                          classList={
                            column.id === 'class' ? '' : 'dropdown-sort-icon'
                          }
                        />
                      )}
                      <div
                        className={`flex items-center gap-3 header-cell ${headerClassList} ${
                          column.columnClassList ? column.columnClassList : ''
                        }`}
                      >
                        <div
                          className={`flex justify-end gap-2 header-info items-center  ${
                            column.columnClassList ? column.columnClassList : ''
                          }`}
                        >
                          {column.label}
                          {column.subColumns && column.subColumns.length && (
                            <div className="[writing-mode:vertical-lr] [text-orientation:mixed] rotate-[180deg] flex flex-col-reverse w-full">
                              {column.subColumns.map((subCol) => (
                                <div
                                  className="flex-1 sub-col-wrap flex items-center gap-1 px-[16px] pb-5 pt-5 relative"
                                  onClick={
                                    subCol.isSortable
                                      ? () => handleSort(subCol.id)
                                      : undefined
                                  }
                                >
                                  {/* sub column wise sorting */}
                                  {subCol.isSortable &&
                                  sortedColumn !== subCol.id ? (
                                    <div className="flex h-6 w-6 items-center justify-center absolute top-4 left-5">
                                      <MLCustomIcon
                                        iconName="Up-down-sort-arrow"
                                        height="16px"
                                        width="16px"
                                      />
                                    </div>
                                  ) : subCol.isSortable &&
                                    sortDirection === 'asc' &&
                                    sortedColumn === subCol.id ? (
                                    <div className="flex h-6 w-6 items-center justify-center absolute top-4 left-5">
                                      <MLCustomIcon
                                        iconName="Up-sort-arrow"
                                        height="22px"
                                        width="22px"
                                      />
                                    </div>
                                  ) : (
                                    subCol.isSortable &&
                                    sortDirection === 'desc' &&
                                    sortedColumn === subCol.id && (
                                      <div className="flex h-6 w-6 items-center justify-center absolute top-4 left-5">
                                        <MLCustomIcon
                                          iconName="Down-sort-arrow"
                                          height="16px"
                                          width="16px"
                                        />
                                      </div>
                                    )
                                  )}
                                  <div
                                    className={`font-normal`}
                                    key={subCol.id}
                                  >
                                    {subCol.label}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {column.id === 'actionBtnRow' && column.hasHeadingBtns
                            ? column.headingButtons.map((btn) => (
                                <MLButton
                                  btnText={btn.button_text}
                                  btnSize={'medium'}
                                  btnVariant={'outlined'}
                                  key={btn.text}
                                  classList={`btn-outline-${btn.button_type} ${
                                    column.selectedBtnType !== '' &&
                                    column.selectedBtnType === btn.button_type
                                      ? column.selectedBtnType + '-selected'
                                      : ''
                                  }`}
                                  onClickFunc={() =>
                                    column.headingBtnHandler(btn)
                                  }
                                />
                              ))
                            : column.headingButtons &&
                              column.headingButtons.map((btn) => (
                                <div className="min-w-24 text-center">
                                  {btn.button_text}
                                </div>
                              ))}
                        </div>

                        {column.isSortable && sortedColumn !== column.id ? (
                          <div
                            className={`flex h-6 w-6 items-center justify-center icon-wrap${
                              column.sortIconClassList
                                ? column.sortIconClassList
                                : ''
                            }`}
                          >
                            <MLCustomIcon
                              iconName="Up-down-sort-arrow"
                              height="16px"
                              width="16px"
                            />
                          </div>
                        ) : column.isSortable &&
                          sortDirection === 'asc' &&
                          sortedColumn === column.id ? (
                          <div
                            className={`flex h-6 w-6 items-center justify-center icon-wrap${
                              column.sortIconClassList
                                ? column.sortIconClassList
                                : ''
                            }`}
                          >
                            <MLCustomIcon
                              iconName="Up-sort-arrow"
                              height="22px"
                              width="22px"
                            />
                          </div>
                        ) : (
                          column.isSortable &&
                          sortDirection === 'desc' &&
                          sortedColumn === column.id && (
                            <div
                              className={`flex h-6 w-6 items-center justify-center icon-wrap${
                                column.sortIconClassList
                                  ? column.sortIconClassList
                                  : ''
                              }`}
                            >
                              <MLCustomIcon
                                iconName="Down-sort-arrow"
                                height="16px"
                                width="16px"
                              />
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!isDataLoading &&
              isManageNoFoundRows &&
              sortedRows.length === 0 ? (
                <Typography variant="h5" className={noFoundRowClassList}>
                  {showNoFoundRowMsg}
                </Typography>
              ) : (
                sortedRows?.map((row, index) => {
                  return (
                    <>
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.identifier}
                        className={`${
                          isRowClickable && row.rowClickVars && 'cursor-pointer'
                        } ${row.cellBenchmark ? row.cellBenchmark : ''}`}
                        onClick={
                          isRowClickable
                            ? row.rowClickVars
                              ? () => rowHandler(row.rowClickVars)
                              : undefined
                            : undefined
                        }
                      >
                        {columns.map((column) => {
                          const value = row[column.id];
                          if (column.subColumns) {
                            return (
                              <TableCell className="!p-0">
                                <div className="flex w-full items-center ">
                                  {column.subColumns.map((col) => {
                                    if (col.id === 'date') {
                                      return (
                                        <div className="w-6/12 min-w-[120px] text-center px-4 py-[22px] inner-cell min-h-[60px]">
                                          {row[column.subColumns[1].id]?.date}
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <div
                                          className={`w-6/12 min-w-[120px] text-center px-4 py-[22px] inner-cell  min-h-[60px] ${
                                            row[col.id]?.benchmark
                                          }`}
                                        >
                                          {row[col.id]?.score
                                            ? row[col.id]?.score
                                            : '-'}
                                        </div>
                                      );
                                    }
                                  })}
                                </div>
                              </TableCell>
                            );
                          }

                          if (column.id === 'actionBtn' && column?.buttons) {
                            // Handle the action button column separately
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <div className="flex items-center gap-2 flex-row justify-end">
                                  {column?.buttons
                                    ?.filter(
                                      (b) =>
                                        b?.requiredAccess === undefined ||
                                        row?.user_access_level === undefined ||
                                        b?.requiredAccess.includes(
                                          row?.user_access_level
                                        )
                                    )
                                    .map((btn) => (
                                      <MLButton
                                        btnText={
                                          btn?.text
                                            ? row.isAlreadyAdded
                                              ? 'Added'
                                              : btn.text
                                            : 'Button'
                                        }
                                        btnSize={
                                          column.btnSize
                                            ? column.btnSize
                                            : 'medium'
                                        }
                                        btnVariant={
                                          row.isAlreadyAdded
                                            ? 'contained'
                                            : 'outlined'
                                        }
                                        classList={`btn-outlined ${
                                          row.btnClassList
                                        } 
                                      ${
                                        row.isWheldallProgRow &&
                                        btn.text !== row.isBtnShow
                                          ? '!hidden'
                                          : ''
                                      }
                                      `}
                                        startIcon={
                                          btn.btnIcon
                                            ? !row.isAlreadyAdded && true
                                            : false
                                        }
                                        btnDisable={row.isEnableTotalScore}
                                        iconName={
                                          btn.btnIcon
                                            ? !row.isAlreadyAdded && btn.btnIcon
                                            : ''
                                        }
                                        key={btn.text}
                                        onClickFunc={() => btn.cellRender(row)}
                                      />
                                    ))}
                                </div>
                              </TableCell>
                            );
                          }

                          // handle buttons row (score buttons)
                          if (column.id === 'actionBtnRow') {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <div
                                  className={`flex items-center gap-2 flex-row justify-end w-fit ml-auto ${
                                    row.isTextbox ? 'is-textbox' : ''
                                  } ${
                                    row.result ? row.result + '-textbox' : ''
                                  }`}
                                >
                                  {row.isTextbox &&
                                    !row.textboxDisableAnswers.includes(
                                      row.result
                                    ) &&
                                    row.result !== null && (
                                      <MLTextField
                                        textFieldId={`outlined-basic-${row.wordNo}`}
                                        placeholder="Given Answer"
                                        variant="outlined"
                                        setTextFieldvalue={(newValue) =>
                                          column.textboxHandler(row, newValue)
                                        }
                                        defaultVal={
                                          row.givenAnswer ? row.givenAnswer : ''
                                        }
                                        autoFocus={true}
                                        isDisabled={
                                          row.isDisabled
                                            ? row.isDisabled
                                            : false
                                        }
                                        handleBlur={(e) =>
                                          column.handleTextInput(
                                            row,
                                            e.target.value
                                          )
                                        }
                                      />
                                    )}
                                  {row?.actionBtnRow?.map((btn) => (
                                    <div className="min-w-24 flex items-center justify-center">
                                      <MLButton
                                        btnText={
                                          btn.isIconBtn ? '' : btn?.button_text
                                        }
                                        btnSize={'medium'}
                                        btnVariant={'outlined'}
                                        classList={`btn-outline-${
                                          btn.button_type
                                        } ${
                                          row.result === btn.button_type
                                            ? btn.button_type + '-selected'
                                            : ''
                                        } ${column.btnClassList} ${
                                          btn.btnClassList
                                            ? btn.btnClassList
                                            : ''
                                        }`}
                                        key={btn.text}
                                        btnDisable={
                                          row.isDisabled
                                            ? row.isDisabled
                                            : false
                                        }
                                        onClickFunc={() =>
                                          column.rowBtnHandler(
                                            row,
                                            btn.button_type
                                          )
                                        }
                                        startIcon={true}
                                        iconName={btn.iconName}
                                        iconFill={btn.iconFill}
                                        iconStroke={btn.iconStroke}
                                        iconWidth={
                                          btn.iconName ===
                                            'Self-correct-word' ?? '100%'
                                        }
                                      />
                                    </div>
                                  ))}
                                </div>
                              </TableCell>
                            );
                          }

                          //Handle menu with specific options
                          if (column.id === 'menuBtn') {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <MLMenu
                                  menuOptions={column?.options}
                                  enrolProgId={row.prog_enrol_id}
                                />
                              </TableCell>
                            );
                          }

                          // Handle the program column with its specific logic
                          if (column.id === 'program') {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <div className="program-data-wrap">
                                  {(value ?? []).length === 0
                                    ? '-'
                                    : value?.map((val) => (
                                        <MLTooltip
                                          tooltipPlacement="top"
                                          tooltipTitle={val.longName}
                                        >
                                          <span className="initialLit-data">
                                            {val.shortName}
                                          </span>
                                        </MLTooltip>
                                      ))}
                                </div>
                              </TableCell>
                            );
                          }

                          // Handle the name column with the navigation logic
                          if (
                            column.id === 'name' ||
                            column.id === 'email_address'
                          ) {
                            const navigateTo =
                              column.id === 'name'
                                ? `/home/students/profile/${row.studentID}`
                                : `/home/admin/settings/user-list/profile/${row.user_id}`;
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <NavLink
                                  className="name-text"
                                  to={navigateTo}
                                  target={row?.target}
                                >
                                  {value}
                                </NavLink>
                              </TableCell>
                            );
                          }

                          // Handle the icon column with the navigation logic
                          if (column.id === 'icon') {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <div className="flex item-center justify-center">
                                  <MLTooltip
                                    tooltipTitle={row.icon.toolTip}
                                    tooltipPlacement="top"
                                  >
                                    {row.icon !== null ? (
                                      <div>
                                        <MLCustomIcon
                                          iconName={row.icon.icon}
                                        />
                                      </div>
                                    ) : (
                                      '-'
                                    )}
                                  </MLTooltip>
                                </div>
                              </TableCell>
                            );
                          }

                          //Handle image and text
                          if (column.id === 'section') {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <div className="tester-wrap">
                                  {row.section.img ? (
                                    <img
                                      className="img-wrap"
                                      src={row.section.img}
                                      alt="testerImg"
                                      height={70}
                                      width={70}
                                    />
                                  ) : (
                                    '-'
                                  )}

                                  <span>{row.section.name}</span>
                                </div>
                              </TableCell>
                            );
                          }

                          //Handle icon and text
                          if (column.id === 'textIconCell') {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <div className="flex items-center gap-1 items-wrap">
                                  {row.textIconCell.cellIcon && (
                                    <MLCustomIcon
                                      iconName={
                                        row.textIconCell.cellIcon[0].toUpperCase() +
                                        row.textIconCell.cellIcon?.slice(1)
                                      }
                                      height={22}
                                      width={22}
                                      fill="var(--Blue-60)"
                                    />
                                  )}

                                  <span
                                    className={`${
                                      !row.textIconCell.cellIcon
                                        ? 'pl-[30px]'
                                        : ''
                                    }`}
                                  >
                                    {row.textIconCell.cellText}
                                  </span>
                                </div>
                              </TableCell>
                            );
                          }

                          // Handle the badge column with the navigation logic
                          if (column.id === 'badge') {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <div className="flex item-center justify-start">
                                  <MLTooltip tooltipTitle={row.badgeToolTip}>
                                    <div>
                                      <MLBadge
                                        text={row.badgeText}
                                        color={row.badgeStatus}
                                      />
                                    </div>
                                  </MLTooltip>
                                </div>
                              </TableCell>
                            );
                          }

                          // Handle the progress bar column
                          if (column.id === 'progressBar') {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <div className="flex item-center justify-start">
                                  <MLTooltip tooltipTitle={row.progressToolTip}>
                                    <div id="progressBar" className="w-full">
                                      <MLLinearProgress
                                        progress={row.progressBar}
                                        classList={row.progressBarClassList}
                                      />
                                    </div>
                                  </MLTooltip>
                                </div>
                              </TableCell>
                            );
                          }

                          //Handle access-level to edit permissions
                          if (column.id === 'access_level') {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <MLDropdown
                                  containsPlaceholder={false}
                                  options={row.access_level.permissions}
                                  selectedOption={(newAccess) => {
                                    column.dropdownHandler(row, newAccess);
                                  }}
                                  defaultSelectedOption={
                                    row.access_level.currentPermission
                                  }
                                />
                              </TableCell>
                            );
                          }

                          //handle editable value for textbox
                          if (
                            column.id === 'editableScore' ||
                            column.id === 'comment-textbox'
                          ) {
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                className={`${
                                  column.id !== 'comment-textbox' &&
                                  row.focusIndex === index
                                    ? 'activeTextbox'
                                    : ''
                                } ${
                                  column.id !== 'comment-textbox' &&
                                  row.errorTextbox.index === index
                                    ? 'errorTextbox'
                                    : ''
                                }`}
                              >
                                {!row.isEnableTotalScore &&
                                column.id !== 'comment-textbox' ? (
                                  row.editableScore >= 0 &&
                                  row.asmtResultId !== null ? (
                                    `${row.editableScore} / ${row.max_score}`
                                  ) : (
                                    column.id !== 'comment-textbox' && '-'
                                  )
                                ) : (column.id !== 'comment-textbox' &&
                                    !row.editableScore) ||
                                  (column.id !== 'comment-textbox' &&
                                    row.asmtResultId &&
                                    row.isTotalScoreOnly) ||
                                  (column.id === 'comment-textbox' &&
                                    row['student-name'] !== 'Averages:') ? (
                                  <div
                                    className={`relative w-fit ${column.textfieldClassList}`}
                                  >
                                    {column.id !== 'comment-textbox' &&
                                      row.errorTextbox.index === index && (
                                        <span className="min-w-[190px] max-w-[190px] absolute top-[-65px] left-0 translate-x-[50%] rounded-lg p-2  z-20 bg-red-700 text-white whitespace-pre-line text-sm flex">
                                          {row.errorTextbox.message}
                                        </span>
                                      )}
                                    <MLTextField
                                      key={`${row?.studentId}-${column.id}-${
                                        row?.groupComment ?? ''
                                      }`}
                                      placeholder={`${
                                        column.id === 'comment-textbox'
                                          ? 'Add comment'
                                          : `${row.editableScore} / ${row.max_score}`
                                      } `}
                                      textFieldId={`outlined-basic-${index}`}
                                      variant="standard"
                                      setTextFieldvalue={(newvalue, index) => {
                                        column.textBoxvalueHander(
                                          row.uniqueStudentId,
                                          newvalue,
                                          index
                                        );
                                      }}
                                      defaultVal={
                                        column.id === 'comment-textbox'
                                          ? row.groupComment
                                            ? row.groupComment
                                            : ''
                                          : ''
                                      }
                                      isMultiline={
                                        column.id === 'comment-textbox'
                                          ? true
                                          : false
                                      }
                                      autoFocus={
                                        column.id === 'comment-textbox'
                                          ? row.focusIndex
                                            ? true
                                            : false
                                          : row.focusIndex === index
                                      }
                                      handleKeyDown={(event) => {
                                        if (
                                          event.key === 'Tab' ||
                                          event.key === 'Enter'
                                        ) {
                                          column.textBoxEventHander(
                                            row,
                                            event,
                                            index
                                          );
                                        }
                                      }}
                                      handleBlur={(event) => {
                                        if (column.id === 'comment-textbox') {
                                          column.textBoxBlurEventHander(
                                            row,
                                            event,
                                            true,
                                            index
                                          );
                                        }
                                      }}
                                    />
                                  </div>
                                ) : (
                                  column.id !== 'comment-textbox' &&
                                  `${row.editableScore} / ${row.max_score}`
                                )}
                              </TableCell>
                            );
                          }

                          //handle icon click
                          if (
                            column.id === 'actionIcon' ||
                            column.id === 'startActionIcon'
                          ) {
                            let iconName,
                              iconFill,
                              iconWidth,
                              tooltip,
                              iconHeight;

                            switch (column.id) {
                              case 'actionIcon':
                                iconName = row.iconName;
                                iconFill = 'var(--Blue-100)';
                                iconHeight = row.iconHeight;
                                iconWidth = row.iconWidth;
                                tooltip = row.iconTooltipText;
                                break;
                              case 'startActionIcon':
                                iconName = row.startIcon;
                                iconFill = `var(${row.startIconFill})`;
                                iconHeight = row.startIconHeight;
                                iconWidth = row.startIconWidth;
                                tooltip = row.startIconTooltipText;
                                break;
                              default:
                                break;
                            }
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                className="!font-bold uppercase"
                              >
                                <MLTooltip
                                  tooltipPlacement="top"
                                  tooltipTitle={tooltip}
                                >
                                  <div
                                    className={`flex items-center justify-end w-fit mr-auto action-icon-hover cursor-pointer relative ${row.iconClassList}`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      column.iconHandler(row);
                                    }}
                                  >
                                    <MLCustomIcon
                                      iconName={iconName}
                                      width={iconWidth}
                                      height={iconHeight}
                                      fill={iconFill}
                                    />
                                  </div>
                                </MLTooltip>
                              </TableCell>
                            );
                          }

                          //Manage row for total
                          if (row.key === 'Total') {
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                className="!font-bold uppercase"
                              >
                                {value}
                              </TableCell>
                            );
                          }
                          //Manage cell color according to benchmark
                          if (column.hasCellColor) {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <div
                                  className={`cell-data ${
                                    value?.benchmark
                                      ? value?.benchmark
                                      : 'no-bechmark'
                                  }`}
                                >
                                  {value?.score}
                                </div>
                              </TableCell>
                            );
                          }

                          //Handle student status progress
                          if (column.id === 'statusProgress') {
                            let statuses = [...row.statusProgress];
                            const sortedStatus = statuses.sort(
                              (a, b) => b.status_seq_no - a.status_seq_no
                            );

                            let segments = [];
                            let totalSegment = 0;

                            sortedStatus.map((status) => {
                              totalSegment = totalSegment + status.students;
                            });

                            sortedStatus.map((status, index) => {
                              const studentStatusPercent = `${
                                (status.students * 100) / totalSegment
                              }%`;
                              segments.push({
                                status: status.status_desc,
                                count: status.students,
                                percentCount: studentStatusPercent,
                              });
                            });

                            return (
                              <TableCell key={column.id} align={column.align}>
                                <MultiSegmentProgressBar segments={segments} />
                              </TableCell>
                            );
                          }

                          // Handle text with page number
                          if (column.id === 'word') {
                            return (
                              <TableCell>
                                {row.showPageNo ? (
                                  <div className="flex items-center gap-10">
                                    <span>{row.wordNo}.</span>
                                    <span>{row.word}</span>
                                  </div>
                                ) : (
                                  row.word
                                )}
                              </TableCell>
                            );
                          }

                          //manage chcekbox
                          if (column.id === 'checkbox') {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <MLCheckbox
                                  isDisabled={row.checkboxDisabled}
                                  isChecked={row.isChecked}
                                  setCheckedStatus={() =>
                                    column.checkboxHandlerFunc(row)
                                  }
                                />
                              </TableCell>
                            );
                          }
                          // Default case for other columns
                          else {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <div className="cell-data">{value}</div>
                              </TableCell>
                            );
                          }
                        })}
                      </TableRow>
                      {row.error && (
                        <TableRow className="error-info">
                          <TableCell colSpan={columns.length}>
                            <div
                              className={`w-fit px-3 rounded-md  ${row.error.errorType}`}
                            >
                              {row.error.message}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
};

export default MLTable;
